import L from 'leaflet'
import GeoJsonAdapter from './geoJsonAdapter'
import * as turf from '@turf/turf'
import CityTopoJsonHelper from '@/libs/static/municipio/topoJsonHelper'
import StateTopoJsonHelper from '@/libs/static/uf/topoJsonHelper'
import { getPolygonDiff, getPolygonUnion, isPointInsideFeature } from './utils'

const DEFAULTS = {
  geoJsonLayer: {
    fill: true,
    color: '#3487FC',
    fillColor: '#3487FC',
    fillOpacity: 0.2
  },
  selectedCarLayer: {
    fill: true,
    color: '#009F61',
    fillColor: '#009F61',
    fillOpacity: 0.2
  },
  carLayer: {
    baseUrl: 'https://geoserver.car.gov.br/geoserver/sicar/wms',
    layers: 'sicar:sicar_imoveis_pa',
    format: 'image/png',
    transparent: true
  }
}

export default class RegistrationMapManager {
  _mapManager = null
  _map = null
  _drawItemsGroup = null
  _hasUserLayer = false
  _hasDrawnLayer = false
  _onCarInfoChange = null
  _selectedCarGroup = null
  _enableDrawingControl = false
  _buttonAddUserLayer = null
  _buttonAddDrawnLayer = null
  _isLoading = false
  _stateGeoJson = null
  _isDrawingRestricted = false
  _clickHandler = null
  _vectorizedArea = null

  constructor (mapManager, onCarInfoChangeCb, onlyView = false) {
    this._mapManager = mapManager
    this._map = mapManager.map
    this._drawItemsGroup = mapManager.drawItemsGroup
    this._onCarInfoChange = onCarInfoChangeCb
    this._init(onlyView)
  }

  _init (onlyView) {
    if (!onlyView) {
      this._addCustomControls()
    }
    CityTopoJsonHelper.getTopoJson().then((citiesLayer) => {
      this._citiesLayer = citiesLayer
      this._map.addLayer(citiesLayer)
      if (!this.layers?.length) {
        this._map.fitBounds(citiesLayer.getBounds())
      }
    })
    StateTopoJsonHelper.getTopoJson().then((stateLayer) => {
      this._stateGeoJson = stateLayer.toGeoJSON()
    })
  }

  _addCustomControls () {
    const EnableDrawingControl = L.Control.extend({
      options: {
        position: 'topright'
      },
      onAdd: map => {
        const container = L.DomUtil.create('div', 'sedap-base-map__drawing-custom-control')
        const button = L.DomUtil.create('button', 'sedap-base-map__custom-control-btn', container)
        this._buttonAddDrawnLayer = button

        button.innerHTML = '<i class="fas fa-pencil-alt"></i>'
        button.title = 'Ativar/Desativar desenho'
        L.DomEvent.on(button, 'click', (e) => {
          e.preventDefault()
          if (this._hasDrawnLayer) return
          this._toggleDrawing(!this._enableDrawingControl)
          this._enableDrawingControl = !this._enableDrawingControl
        })
        return container
      }
    })

    const shapeContainer = L.DomUtil.create('div', 'sedap-base-map__shape-custom-control')
    const ImportShapeControl = L.Control.extend({
      options: {
        position: 'topleft'
      },
      onAdd: map => {
        const input = L.DomUtil.create('input', 'sedap-base-map__custom-control-input', shapeContainer)
        input.type = 'file'
        input.accept = '.zip,.kml'
        input.style.display = 'none'

        L.DomEvent.on(input, 'change', (e) => {
          const file = e.target.files[0]
          this._addImportedLayer(file)
          e.target.value = ''
        })

        const button = L.DomUtil.create('button', 'sedap-base-map__custom-control-btn', shapeContainer)
        this._buttonAddUserLayer = button

        button.innerHTML = '<i class="fas fa-upload"></i>'
        button.title = 'Importar camada'
        L.DomEvent.on(button, 'click', (e) => {
          e.preventDefault()
          if (this._hasDrawnLayer) return
          input.click()
        })
        return shapeContainer
      }
    })

    const RemoveShapeControl = L.Control.extend({
      options: {
        position: 'topleft'
      },
      onAdd: map => {
        const button = L.DomUtil.create('button', 'sedap-base-map__custom-control-btn', shapeContainer)

        button.innerHTML = '<i class="fas fa-trash"></i></button>'
        button.title = 'Remover camada'
        L.DomEvent.on(button, 'click', (e) => {
          e.preventDefault()
          if (this._hasDrawnLayer) return
          this._removeImportedLayer()
        })
        return shapeContainer
      }
    })

    this._map.addControl(new EnableDrawingControl())
    this._map.addControl(new ImportShapeControl())
    this._map.addControl(new RemoveShapeControl())
  }

  _updateStateButtonsAddLayer () {
    if (this._buttonAddUserLayer) this._buttonAddUserLayer.disabled = this._hasUserLayer || this._hasDrawnLayer
    if (this._buttonAddDrawnLayer) this._buttonAddDrawnLayer.disabled = this._hasUserLayer || this._hasDrawnLayer
  }

  _addCarLayer (boundary, geoJSON) {
    const carLayer = L.tileLayer.wms(DEFAULTS.carLayer.baseUrl, {
      layers: DEFAULTS.carLayer.layers,
      format: DEFAULTS.carLayer.format,
      transparent: DEFAULTS.carLayer.transparent,
      bounds: boundary,
      CQL_FILTER: this._setCQLFilter(geoJSON)
    })

    carLayer.options.name = 'carLayer'
    this._map.addLayer(carLayer)
    this._onCarInfoChange(true)
  }

  _setCQLFilter (geoJson) {
    const geometry = geoJson.type === 'FeatureCollection'
      ? geoJson.features[0].geometry
      : geoJson.geometry || geoJson

    let turfGeom
    try {
      turfGeom = turf.geometry(geometry.type, geometry.coordinates)
    } catch (e) {
      console.error('Erro ao converter geometria para Turf:', e)
      return null
    }

    const feature = turf.feature(turfGeom)

    const simplified = turf.simplify(feature, {
      tolerance: 0.001,
      mutate: false
    })

    const simplifiedGeometry = simplified.geometry

    let cqlFilter = 'INTERSECTS(geo_area_imovel, '

    switch (simplifiedGeometry.type) {
      case 'Polygon':
        const coordsPolygon = simplifiedGeometry.coordinates[0]
          .map(coord => `${coord[0]} ${coord[1]}`)
          .join(',')
        cqlFilter += `POLYGON((${coordsPolygon}))`
        break

      case 'MultiPolygon':
        const coordsMultiPolygon = simplifiedGeometry.coordinates.map(polygon =>
          polygon[0].map(coord => `${coord[0]} ${coord[1]}`).join(',')
        ).join('),(')
        cqlFilter += `MULTIPOLYGON(((${coordsMultiPolygon})))`
        break

      default:
        console.error('Tipo de geometria não suportado:', simplifiedGeometry.type)
        return null
    }

    cqlFilter += ')'

    return cqlFilter
  }

  async _fetchCARInfo (e) {
    this._mapManager._enableLoading()
    const map = e.target
    let shouldTrigger = false
    map.eachLayer(layer => {
      if (layer.options.name === 'userLayer' || layer.options.name === 'drawnLayer') {
        shouldTrigger = layer.getBounds().contains(e.latlng)
      }
    })

    if (!shouldTrigger) {
      this._mapManager._disableLoading()
      return
    }
    const point = map.latLngToContainerPoint(e.latlng, map.getZoom())
    const size = map.getSize()
    const bounds = map.getBounds()
    const sw = map.options.crs.project(bounds.getSouthWest())
    const ne = map.options.crs.project(bounds.getNorthEast())

    const url = 'https://geoserver.car.gov.br/geoserver/sicar/wms' +
    '?service=WMS' +
    '&version=1.1.1' +
    '&request=GetFeatureInfo' +
    '&layers=sicar:sicar_imoveis_pa' +
    '&query_layers=sicar:sicar_imoveis_pa' +
    '&bbox=' + sw.x + ',' + sw.y + ',' + ne.x + ',' + ne.y +
    '&width=' + size.x +
    '&height=' + size.y +
    '&srs=EPSG:3857' +
    '&x=' + Math.floor(point.x) +
    '&y=' + Math.floor(point.y) +
    '&info_format=application/json'

    let result = null

    try {
      await fetch(url)
        .then(r => r.json())
        .then(data => {
          this._applySelectedCarLayer(data)
          result = {
            ...data.features[0]?.properties,
            features: data.features
          }
        })
    } catch (error) {
      throw new Error(`Erro ao buscar informações do CAR: ${error}`)
    } finally {
      this._mapManager._disableLoading()
    }

    return result
  }

  async _addLayerByCAR (car) {
    this._mapManager._enableLoading()
    const url = 'https://geoserver.car.gov.br/geoserver/sicar/wfs' +
      '?service=WFS' +
      '&version=2.0.0' +
      '&request=GetFeature' +
      '&typeName=sicar:sicar_imoveis_pa' +
      '&outputFormat=application/json' +
      '&srsName=EPSG:3857' +
      `&CQL_FILTER=cod_imovel='${car.replace(/\./g, '')}'`

    let result = null

    try {
      await fetch(url)
        .then(r => r.json())
        .then(data => {
          this._applySelectedCarLayer(data)

          result = {
            ...data.features[0]?.properties,
            features: data.features
          }
        })
    } catch (error) {
      throw new Error(`Erro ao buscar informações do CAR: ${error}`)
    } finally {
      this._mapManager._disableLoading()
    }

    return result
  }

  _addSelectedCARGroup () {
    if (!this._selectedCarGroup) {
      this._selectedCarGroup = L.featureGroup()
      this._selectedCarGroup.addTo(this._map)
    }
  }

  _removeSelectedCarGroup () {
    if (!this._selectedCarGroup) return
    this._selectedCarGroup.clearLayers()
    this._selectedCarGroup = null
  }

  _applySelectedCarLayer (data) {
    const { features, type } = data

    const codImovel = features[0].properties.cod_imovel

    this._addSelectedCARGroup()
    let hasLayer = false

    this._selectedCarGroup.eachLayer(layer => {
      if (layer.options.codImovel === codImovel) {
        hasLayer = true
      }
    })

    if (hasLayer) return

    const geoJson = L.geoJSON(
      { features, type },
      { style: DEFAULTS.selectedCarLayer,
        codImovel,
        coordsToLatLng: function (coords) {
          return L.Projection.SphericalMercator.unproject(L.point(coords[0], coords[1]))
        }}
    )

    this._selectedCarGroup.addLayer(geoJson)
  }

  async _convertToGeoJson (file) {
    try {
      const adapter = new GeoJsonAdapter(file)
      const geoJson = await adapter.adapt()

      return geoJson
    } catch (error) {
      console.error(error)
      return null
    }
  }

  _toggleDrawing (value) {
    if (this._hasUserLayer || this._hasDrawnLayer) return

    this._mapManager.toggleDrawing(value)

    if (!this._enableDrawingControl) {
      this._addDrawingRestrictions()
    }
  }

  async _addImportedLayer (layer) {
    this._mapManager._enableLoading()
    const geoJson = await this._convertToGeoJson(layer)

    if (!geoJson) {
      this._mapManager._disableLoading()
      return
    }
    const isInside = turf.booleanWithin(geoJson.features[0], this._stateGeoJson.features[0])

    if (!isInside) {
      this._mapManager._disableLoading()
      this._mapManager._showAlert('O imóvel deve estar dentro dos limites do estado do Pará.')
      return
    }

    this._layers = L.geoJSON(geoJson, { style: DEFAULTS.geoJsonLayer, name: 'userLayer' }).addTo(this._map)
    let areaTotal = 0
    geoJson.features.forEach(feature => {
      const geom = feature.geometry

      if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((coordinates) => {
          let polygon = { type: 'Polygon', coordinates }
          areaTotal += turf.area(polygon)
        })
      } else {
        areaTotal += turf.area(geom)
      }
    })
    this._vectorizedArea = (areaTotal / 10000)

    const bounds = this._layers.getBounds()
    this._addCarLayer(bounds, geoJson)
    this._map.fitBounds(bounds)

    this._enableDrawingControl = false
    this._toggleDrawing(this._enableDrawingControl)
    this._hasUserLayer = true

    this._updateStateButtonsAddLayer()

    this._mapManager._disableLoading()
  }

  _removeImportedLayer () {
    this._map.eachLayer(layer => {
      if (layer.options.name === 'userLayer') {
        this._map.removeLayer(layer)
        this._removeCarLayer()
        this._hasUserLayer = false
        this._removeSelectedCarGroup()
      }
    })

    this._mapManager.setDefaultMapPosition()

    this._updateStateButtonsAddLayer()
  }

  _removeCarLayer () {
    this._map.eachLayer(layer => {
      if (layer.options.name === 'carLayer') {
        this._map.removeLayer(layer)
        this._onCarInfoChange(false)
        this._removeSelectedCarGroup()
      }
    })
  }

  _addDrawingRestrictions () {
    this._clickHandler = (e) => {
      if (this._isDrawingRestricted) {
        const isInside = isPointInsideFeature(e.latlng, this._stateGeoJson.features[0])
        if (!isInside) {
          e.originalEvent.stopPropagation()
          e.originalEvent.preventDefault()
          this._mapManager._showAlert('O imóvel deve estar dentro dos limites do estado do Pará.')
          return false
        }
      }
    }
    this._map.on('click', this._clickHandler)
    this._map.on('draw:drawstart', () => {
      this._isDrawingRestricted = true
    })
    this._map.on('draw:drawstop', () => {
      this._removeDrawingRestrictions()
    })
    this._map.on('draw:canceled', () => {
      this._removeDrawingRestrictions()
    })
  }

  _removeDrawingRestrictions () {
    this._isDrawingRestricted = false
    if (this._clickHandler) {
      this._map.off('click', this._clickHandler)
      this._clickHandler = null
    }
  }

  _applyDrawToStateBoundaries (layer) {
    const copyLayer = layer

    this._drawItemsGroup.removeLayer(layer)

    let drawnArea = this._drawWithinStateLimits(copyLayer)

    if (!drawnArea) {
      return
    }

    drawnArea = this._checkOverlappingAreas(drawnArea)

    const newDrawnArea = L.geoJSON(drawnArea, { style: DEFAULTS.geoJsonLayer, name: 'drawnLayer' })

    const layers = newDrawnArea.getLayers()

    layers.forEach((layer) => {
      const geom = layer.feature.geometry

      if (geom.type === 'MultiPolygon') {
        geom.coordinates.forEach((coordinates) => {
          let polygon = { type: 'Polygon', coordinates }

          L.geoJSON(polygon, {
            style: DEFAULTS.geoJsonLayer,
            name: 'drawnLayer',
            onEachFeature: (_, cLayer) => {
              cLayer.options.name = 'drawnLayer'
              this._drawItemsGroup.addLayer(cLayer)
            }
          })
        })
      } else {
        layer.options.name = 'drawnLayer'
        this._drawItemsGroup.addLayer(layer)
      }
    })

    return newDrawnArea
  }

  _drawWithinStateLimits (newLayer) {
    newLayer = newLayer.toGeoJSON()

    if (newLayer.type === 'FeatureCollection') {
      newLayer = newLayer.features[0]
    }

    const coordLayer = turf.polygon(newLayer.geometry.coordinates)

    const intersections = this._stateGeoJson.features[0].geometry
      .coordinates.map(coords => coords.map(coord => turf.intersect(turf.featureCollection([turf.polygon([coord]), coordLayer])))[0])
      .filter(Boolean)

    return intersections.length > 1 ? turf.union(turf.featureCollection(intersections)) : intersections[0]
  }

  _checkOverlappingAreas (currLayer) {
    let result = currLayer

    this._drawItemsGroup.eachLayer((layer) => {
      if (!layer.toGeoJSON) return

      const layerGeoJSON = layer.toGeoJSON()
      let features = layerGeoJSON

      if (layerGeoJSON.type === 'FeatureCollection') {
        features = layerGeoJSON.features[0]
      }

      if (layer.options.style.color === currLayer.properties.color) {
        const union = getPolygonUnion(features, result)

        if (union) {
          this._drawItemsGroup.removeLayer(layer)
          return (result = union)
        }
      }

      const difference = getPolygonDiff(features, result)

      if (difference) {
        return (result = difference)
      }
    })
    return result
  }

  get map () {
    return this._map
  }

  get layers () {
    const allowedLayers = ['drawnLayer', 'userLayer']
    let layers = []

    this._map.eachLayer(layer => {
      if (allowedLayers.includes(layer?.options?.name)) {
        const duplicatedLayer = layers.find(l => l.options.name === layer.options.name)
        if (duplicatedLayer) {
          if (duplicatedLayer.defaultOptions) {
            layers = layers.filter(l => l.options.name !== layer.options.name)
            layers.push(layer)
          }
        } else {
          layers.push(layer)
        }
      }
    })

    layers = layers.map(layer => {
      // Upload de arquivo shapefile (Já vem no formato FeatureCollection)
      if (layer.toGeoJSON().type === 'FeatureCollection') {
        return {...layer.toGeoJSON(), area: this._vectorizedArea}
      } else {
        // Desenho de polígono (Formatado para FeatureCollection para manter a mesma estrutura)
        return {
          type: 'FeatureCollection',
          features: [layer.toGeoJSON()],
          area: this._vectorizedArea
        }
      }
    })

    return layers
  }

  async _addSavedLayer (geoJson) {
    if (!geoJson) return

    this._layers = L.geoJSON(geoJson, { style: DEFAULTS.geoJsonLayer, name: 'userLayer' }).addTo(this._map)

    const bounds = this._layers.getBounds()
    this._addCarLayer(bounds, geoJson)
    this._map.fitBounds(bounds)

    this._enableDrawingControl = false
    this._toggleDrawing(this._enableDrawingControl)
    this._hasUserLayer = true

    this._updateStateButtonsAddLayer()
  }

  async fetchCarInfo (e) {
    try {
      const result = await this._fetchCARInfo(e)
      return result
    } catch (error) {
      console.error(error)
      return null
    }
  }

  async addLayerByCAR (car) {
    try {
      const result = await this._addLayerByCAR(car)
      return result
    } catch (error) {
      console.error(error)
      return null
    }
  }

  handleDrawCreated (e) {
    const layer = e.layer
    layer.options.name = 'drawnLayer'

    const clipped = this._applyDrawToStateBoundaries(layer)?.toGeoJSON?.()

    if (!clipped) {
      this._mapManager._showAlert('O imóvel deve estar dentro dos limites do estado do Pará.')
      return
    }

    const bounds = this._drawItemsGroup.getBounds()

    const geoJson = clipped.type === 'FeatureCollection'
      ? clipped
      : { type: 'FeatureCollection', features: [clipped] }

    this._addCarLayer(bounds, geoJson)
    this._map.fitBounds(bounds)

    this._hasDrawnLayer = true
    this._updateStateButtonsAddLayer()
  }

  handleDrawDeleted (e) {
    this._hasDrawnLayer = false
    this._removeCarLayer()
    this._mapManager.setDefaultMapPosition()

    this._updateStateButtonsAddLayer()
  }

  removeSelectedCarLayer (carCode) {
    if (!this._selectedCarGroup) return

    this._selectedCarGroup.eachLayer(layer => {
      if (layer.options.codImovel === carCode) {
        this._selectedCarGroup.removeLayer(layer)
      }
    })
  }

  addPropertyLayers (geo) {
    geo.geoJsonList.forEach(geoJson => {
      this._addSavedLayer(geoJson)
      const layer = L.geoJSON(geoJson, { name: 'userLayer' })
      layer.addTo(this._map)
      this._map.fitBounds(layer.getBounds())
    })

    geo.numerosCar.forEach(car => {
      const geoJson = car.geoJson
      this._applySelectedCarLayer({features: geoJson, type: geoJson[0].type})
    })
  }
}



// WEBPACK FOOTER //
// ./src/components(new)/UI/Maps/core/registrationMapManager.js